import { useState, useMemo } from "react";
import {
  Box, Typography, Card, CardContent, CardHeader, Accordion, AccordionSummary, AccordionDetails,
  Grid, TextField, Button, Chip, Link as MuiLink,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SettingsIcon from "@mui/icons-material/Settings";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { useData, useDataCtx } from "../context/DataContext";
import { computeAlerts, DEFAULT_ALERT_CONFIG, AlertConfig, VesselAlert } from "../utils/alertUtils";

interface AlertsProps {
  onNavigateToVessel: (vessel: string) => void;
}

function ThresholdRow({
  label, warnValue, critValue, warnKey, critKey, step, unit, onChange,
}: {
  label: string; warnValue: number; critValue: number;
  warnKey: keyof AlertConfig; critKey: keyof AlertConfig;
  step: number; unit: string; onChange: (key: keyof AlertConfig, val: number) => void;
}) {
  return (
    <Grid container alignItems="center" spacing={2} sx={{ py: 1, borderBottom: "1px solid", borderColor: "divider" }}>
      <Grid item xs={12} sm={4}>
        <Typography variant="body2">{label}</Typography>
      </Grid>
      <Grid item xs={6} sm={4} display="flex" alignItems="center" gap={1}>
        <Typography variant="caption" color="warning.main" fontWeight={600} sx={{ width: 60, textAlign: "right" }}>Warning</Typography>
        <TextField size="small" type="number" value={warnValue} inputProps={{ step, min: 0, style: { width: 70, textAlign: "center" } }}
          onChange={e => onChange(warnKey, parseFloat(e.target.value) || 0)} />
        <Typography variant="caption" color="text.secondary">{unit}</Typography>
      </Grid>
      <Grid item xs={6} sm={4} display="flex" alignItems="center" gap={1}>
        <Typography variant="caption" color="error.main" fontWeight={600} sx={{ width: 60, textAlign: "right" }}>Critical</Typography>
        <TextField size="small" type="number" value={critValue} inputProps={{ step, min: 0, style: { width: 70, textAlign: "center" } }}
          onChange={e => onChange(critKey, parseFloat(e.target.value) || 0)} />
        <Typography variant="caption" color="text.secondary">{unit}</Typography>
      </Grid>
    </Grid>
  );
}

function AlertCard({ level, alerts, onNavigateToVessel }: {
  level: "critical" | "warning"; alerts: VesselAlert[]; onNavigateToVessel: (v: string) => void;
}) {
  const isCrit = level === "critical";
  return (
    <Card variant="outlined" sx={{ borderColor: isCrit ? "error.light" : "warning.light" }}>
      <CardHeader
        avatar={isCrit ? <ErrorOutlineIcon color="error" /> : <WarningAmberIcon color="warning" />}
        title={isCrit ? "Critical" : "Warning"}
        action={<Chip size="small" color={isCrit ? "error" : "warning"} label={`${alerts.length} vessel${alerts.length !== 1 ? "s" : ""}`} sx={{ mt: 1, mr: 1 }} />}
      />
      <CardContent sx={{ pt: 0 }}>
        {alerts.length === 0 ? (
          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 2 }}>
            No vessels meet {level} thresholds.
          </Typography>
        ) : (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2">View list</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ display: "flex", flexDirection: "column", gap: 1, maxHeight: 280, overflowY: "auto" }}>
              {alerts.map(a => (
                <Box key={a.site} sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", p: 1, borderRadius: 1, border: "1px solid", borderColor: "divider" }}>
                  <Box>
                    <Typography variant="body2" fontFamily="monospace" fontWeight={700}>{a.site}</Typography>
                    <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                      {a.reasons.map((r, i) => (
                        <Chip key={i} size="small" label={r.rule} color={r.level === "critical" ? "error" : "warning"} variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                  <MuiLink component="button" variant="caption" onClick={() => onNavigateToVessel(a.site)}>
                    View
                  </MuiLink>
                </Box>
              ))}
            </AccordionDetails>
          </Accordion>
        )}
      </CardContent>
    </Card>
  );
}

export default function Alerts({ onNavigateToVessel }: AlertsProps) {
  const { rawData, siteAggregates } = useData();
  const { thresholds, setThresholds } = useDataCtx();
  const [localCfg, setLocalCfg] = useState<AlertConfig>(thresholds);

  const update = (key: keyof AlertConfig, val: number) => {
    const next = { ...localCfg, [key]: val };
    setLocalCfg(next);
    setThresholds(next);
  };

  const alerts = useMemo(() => computeAlerts(rawData, siteAggregates, localCfg), [rawData, siteAggregates, localCfg]);
  const critical = alerts.filter(a => a.level === "critical");
  const warnings = alerts.filter(a => a.level === "warning");

  return (
    <Box sx={{ p: 2 }}>
      <Box mb={2}>
        <Typography variant="subtitle1" fontWeight={600}>Alerts</Typography>
        <Typography variant="body2" color="text.secondary">
          Fleet anomaly detection — {critical.length} critical, {warnings.length} warning across {siteAggregates.length} vessels
        </Typography>
      </Box>

      <Accordion variant="outlined" sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <SettingsIcon fontSize="small" color="action" />
            <Typography variant="subtitle1" fontWeight={600}>Configuration</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box mb={2}>
            <Typography variant="caption" fontWeight={600} sx={{ textTransform: "uppercase" }} color="text.secondary">Total WAN — σ above fleet mean</Typography>
            <ThresholdRow label="Sigma above fleet mean" warnValue={localCfg.wanWarnSigma} critValue={localCfg.wanCritSigma} warnKey="wanWarnSigma" critKey="wanCritSigma" step={0.1} unit="σ" onChange={update} />
          </Box>
          <Box mb={2}>
            <Typography variant="caption" fontWeight={600} sx={{ textTransform: "uppercase" }} color="text.secondary">Unauthorized traffic — peak day % of WAN</Typography>
            <ThresholdRow label="UnAuth as % of daily WAN" warnValue={localCfg.unauthWarnPct} critValue={localCfg.unauthCritPct} warnKey="unauthWarnPct" critKey="unauthCritPct" step={1} unit="%" onChange={update} />
          </Box>
          <Box mb={2}>
            <Typography variant="caption" fontWeight={600} sx={{ textTransform: "uppercase" }} color="text.secondary">Static device traffic — peak day % of WAN</Typography>
            <ThresholdRow label="Static as % of daily WAN" warnValue={localCfg.staticWarnPct} critValue={localCfg.staticCritPct} warnKey="staticWarnPct" critKey="staticCritPct" step={1} unit="%" onChange={update} />
          </Box>
          <Box mb={1}>
            <Typography variant="caption" fontWeight={600} sx={{ textTransform: "uppercase" }} color="text.secondary">Auth/User sustained usage — consecutive days</Typography>
            <Grid container spacing={2} sx={{ py: 1 }}>
              <Grid item display="flex" alignItems="center" gap={1}>
                <Typography variant="body2">Threshold</Typography>
                <TextField size="small" type="number" value={localCfg.authPerUserGB} inputProps={{ step: 0.1, min: 0, style: { width: 70, textAlign: "center" } }}
                  onChange={e => update("authPerUserGB", parseFloat(e.target.value) || 0)} />
                <Typography variant="caption" color="text.secondary">GB/user/day</Typography>
              </Grid>
              <Grid item display="flex" alignItems="center" gap={1}>
                <Typography variant="caption" color="warning.main" fontWeight={600}>Warning</Typography>
                <TextField size="small" type="number" value={localCfg.consecWarnDays} inputProps={{ step: 1, min: 1, style: { width: 60, textAlign: "center" } }}
                  onChange={e => update("consecWarnDays", parseInt(e.target.value) || 1)} />
                <Typography variant="caption" color="text.secondary">days</Typography>
              </Grid>
              <Grid item display="flex" alignItems="center" gap={1}>
                <Typography variant="caption" color="error.main" fontWeight={600}>Critical</Typography>
                <TextField size="small" type="number" value={localCfg.consecCritDays} inputProps={{ step: 1, min: 1, style: { width: 60, textAlign: "center" } }}
                  onChange={e => update("consecCritDays", parseInt(e.target.value) || 1)} />
                <Typography variant="caption" color="text.secondary">days</Typography>
              </Grid>
            </Grid>
          </Box>
          <Box display="flex" justifyContent="flex-end" pt={1} sx={{ borderTop: "1px solid", borderColor: "divider" }}>
            <Button size="small" variant="outlined" startIcon={<RestartAltIcon />} onClick={() => { setLocalCfg(DEFAULT_ALERT_CONFIG); setThresholds(DEFAULT_ALERT_CONFIG); }}>
              Reset defaults
            </Button>
          </Box>
        </AccordionDetails>
      </Accordion>

      <Box display="flex" flexDirection="column" gap={2}>
        <AlertCard level="critical" alerts={critical} onNavigateToVessel={onNavigateToVessel} />
        <AlertCard level="warning" alerts={warnings} onNavigateToVessel={onNavigateToVessel} />
      </Box>
    </Box>
  );
}
